/**
 * Utility functions for handling sensitive header information securely
 */

// List of sensitive header names that should be masked
export const SENSITIVE_HEADERS = [
  "authorization",
  "x-api-key",
  "api-key",
  "apikey",
  "x-auth-token",
  "auth-token",
  "authentication",
  "bearer",
  "token",
  "secret",
  "password",
  "x-access-token",
  "access-token",
  "private-key",
  "x-secret",
  "x-secret-key",
  "x-auth",
  "x-token",
  "session",
  "session-id",
  "sessionid",
  "cookie",
  "csrf",
  "csrf-token",
  "x-csrf-token"
];

/**
 * Check if a header name is sensitive and should be masked
 * @param headerName The header name to check
 * @returns true if the header is sensitive
 */
export const isSensitiveHeader = (headerName: string): boolean => {
  if (!headerName) return false;
  const lowerName = headerName.toLowerCase();
  return SENSITIVE_HEADERS.some(sensitive => lowerName.includes(sensitive));
};

/**
 * Mask a sensitive value for display
 * @param value The value to mask
 * @param showPartial Whether to show partial value (first and last 4 chars)
 * @returns The masked value
 */
export const maskValue = (value: string, showPartial: boolean = true): string => {
  if (!value) return "";
  
  // For very short values, mask entirely
  if (value.length <= 8) {
    return "•".repeat(value.length);
  }
  
  // Show first 4 and last 4 characters if showPartial is true and value is long enough
  if (showPartial && value.length > 12) {
    return `${value.slice(0, 4)}${"•".repeat(8)}${value.slice(-4)}`;
  }
  
  // Otherwise just show dots
  return "•".repeat(Math.min(value.length, 16));
};

/**
 * Get display value for a header - masks if sensitive
 * @param headerName The header name
 * @param headerValue The header value
 * @param isVisible Whether the value should be shown unmasked
 * @returns The display value (masked if sensitive and not visible)
 */
export const getHeaderDisplayValue = (
  headerName: string,
  headerValue: string,
  isVisible: boolean = false
): string => {
  if (!headerValue) return "";
  
  // If it's not sensitive or explicitly made visible, show the actual value
  if (!isSensitiveHeader(headerName) || isVisible) {
    return headerValue;
  }
  
  // Otherwise, mask it
  return maskValue(headerValue);
};

/**
 * Hook to manage visibility state for sensitive headers
 */
export const useSensitiveHeaderVisibility = () => {
  const [visibleHeaders, setVisibleHeaders] = React.useState<Set<string>>(new Set());

  const toggleHeaderVisibility = React.useCallback((headerKey: string) => {
    setVisibleHeaders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(headerKey)) {
        newSet.delete(headerKey);
      } else {
        newSet.add(headerKey);
      }
      return newSet;
    });
  }, []);

  const isHeaderVisible = React.useCallback((headerKey: string) => {
    return visibleHeaders.has(headerKey);
  }, [visibleHeaders]);

  const clearAllVisibility = React.useCallback(() => {
    setVisibleHeaders(new Set());
  }, []);

  return {
    visibleHeaders,
    toggleHeaderVisibility,
    isHeaderVisible,
    clearAllVisibility
  };
};

// Import React for the hook
import React from "react";
